"use client";

import { useMutation } from "@apollo/client";
import { ModalButtons, Textarea } from "components/ui/v2";
import { gqlUser } from "gql";
import { IUserNotifyInput } from "gql/User/mutations";
import useTranslation from "next-translate/useTranslation";
import { useState } from "react";
import toast from "react-hot-toast";
import { IUser } from "types/User.types";
import type { FormV2Props } from "../form-v2-props";

export const NOTIFY_USER_FORM_ID = "notify-user-form";

export interface NotifyUserModalContentV2Props extends FormV2Props {
  user: IUser;
  hideFooter?: boolean;
  formId?: string;
  /** Prefill the message textarea (e.g. renew reminder). */
  initialText?: string;
}

export function NotifyUserModalContentV2({
  user,
  onClose,
  hideHeader = true,
  hideFooter = false,
  formId = NOTIFY_USER_FORM_ID,
  initialText = "",
}: NotifyUserModalContentV2Props) {
  const { t } = useTranslation("common");
  const [text, setText] = useState<string>(initialText);

  const [userNotify, { loading }] = useMutation<{
    userNotifyInput: IUserNotifyInput;
  }>(gqlUser.mutations.USER_NOTIFY, {
    onCompleted: () => {
      toast.success(t("Notificación enviada correctamente"));
      onClose?.();
    },
    onError: ({ message }) => {
      toast.error(message);
    },
  });

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (text.length === 0) return;
    userNotify({
      variables: {
        userNotifyInput: {
          from: "Certifications",
          text,
          type: "personal",
          to: user?.email,
        },
      },
    });
  };

  return (
    <form id={formId} onSubmit={handleSubmit} className="flex flex-col gap-4">
      {!hideHeader && (
        <div>
          <h3 className="text-[13px] font-semibold text-slate-900 dark:text-zinc-100 sm:text-[15px]">
            {`${t("Enviando notificación al usuario")} ${user?.name} ${user?.lastname}`}
          </h3>
          <p className="mt-1 text-[9px] text-slate-500 dark:text-zinc-400">
            {t(
              "Envía una notificación al usuario por correo para actualizar su certificaciones"
            )}
          </p>
        </div>
      )}
      <Textarea
        value={text}
        onChange={(event) => setText(event.target.value)}
        rows={4}
        placeholder={t("Escribe tu mensaje aquí")}
      />
      <span className="ml-1 flex flex-col text-[9px] text-red-400">
        {t("Se notificará al usuario afectado")}{" "}
        <strong>{t("Usar con precaución")}.</strong>
      </span>
      {!hideFooter ? (
        <ModalButtons
          loading={loading}
          onCancel={onClose}
          cancelLabel={t("Cerrar")}
          submitLabel={t("Enviar")}
        />
      ) : null}
    </form>
  );
}
